import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Vehicle } from '../types';
import { updateVehicle } from '../api';
import { useToast } from '../context/ToastContext';
import VehicleGallery from './VehicleGallery';

// Tid kommer fra DB som "HH:MM:SS" — input type=time vil have "HH:MM"
const timeToInput = (t: string | null | undefined) => t ? t.slice(0, 5) : '';
const inputToTime = (t: string) => t ? `${t}:00` : null;

interface Props {
  vehicle: Vehicle;
  onSaved: (updated: Vehicle) => void;
  viewOnly?: boolean;
}

// Kun http/https tillades — beskytter mod javascript:/data:-schemes i et bruger-indtastet
// felt der senere bruges i href. Tomt felt er "sikkert" (intet link vises).
function isSafeHttpUrl(u: string | null | undefined): boolean {
  if (!u) return true;
  try {
    const parsed = new URL(u);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

// Foreslået slug fra make/model/reg — kan overskrives. Lowercase, ord adskilt af bindestreg.
// Mærke-model-årgang-id, fx "opel-astra-2018-3". Nummerpladen er bevidst ikke med i en offentlig
// URL (Kennith 2026-10-14). Id'et gør den entydig, også når to biler har samme model og årgang.
function suggestSlug(v: Vehicle): string {
  const parts = [v.make, v.model, v.year, v.id].filter(p => p != null && p !== '').map(s => String(s).toLowerCase());
  return parts.join('-')
    .replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa')
    .replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

// Standard-features brugeren kan tikke af. Gemmes som array i listing_features (JSON).
const FEATURE_OPTIONS: { value: string; label: string }[] = [
  { value: 'aircon',        label: 'Aircondition' },
  { value: 'auto',          label: 'Automatgear' },
  { value: 'gps',           label: 'GPS' },
  { value: 'bluetooth',     label: 'Bluetooth' },
  { value: 'cruise',        label: 'Cruise control' },
  { value: 'parkassist',    label: 'Parkeringsassistent' },
  { value: 'trailer',       label: 'Anhængertræk' },
  { value: 'roofbox',       label: 'Tagboks' },
  { value: 'bikerack',      label: 'Cykelholder' },
  { value: 'childseat',     label: 'Barnesæde' },
  { value: 'snowtyres',     label: 'Vinterdæk' },
  { value: 'pet',           label: 'Kæledyr OK' },
  { value: 'smoke',         label: 'Rygning forbudt' },
];

export default function PublicListingTab({ vehicle, onSaved, viewOnly = false }: Props) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [form, setForm] = useState<Vehicle>({
    ...vehicle,
    public_slug: vehicle.public_slug ?? suggestSlug(vehicle),
    operating_mode: vehicle.operating_mode ?? 'platform',
    telematics_source: vehicle.telematics_source ?? 'none',
    listing_features: vehicle.listing_features ?? [],
    listing_pickup_time: timeToInput(vehicle.listing_pickup_time),
    listing_return_time: timeToInput(vehicle.listing_return_time),
  });
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof Vehicle>(field: K, value: Vehicle[K]) =>
    setForm(f => ({ ...f, [field]: value }));

  const toggleFeature = (val: string) => {
    const current = new Set(form.listing_features ?? []);
    if (current.has(val)) current.delete(val); else current.add(val);
    set('listing_features', Array.from(current));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSafeHttpUrl(form.platform_listing_url)) {
      showError('Link til listing skal være en http(s)-adresse');
      return;
    }
    setSaving(true);
    try {
      // Tid-felter konverteres tilbage til "HH:MM:SS"-format som MySQL TIME forventer
      const payload = {
        ...form,
        listing_pickup_time: form.listing_pickup_time && form.listing_pickup_time.length === 5
          ? inputToTime(form.listing_pickup_time)
          : form.listing_pickup_time,
        listing_return_time: form.listing_return_time && form.listing_return_time.length === 5
          ? inputToTime(form.listing_return_time)
          : form.listing_return_time,
      };
      await updateVehicle(vehicle.id!, payload);
      showSuccess(t('modal.saved'));
      onSaved(payload);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  };

  const features = new Set(form.listing_features ?? []);
  const inp = 'input disabled:bg-gray-50 disabled:text-gray-600';

  return (
    <form onSubmit={handleSave} className="p-6 space-y-5">
      {/* Booking-channel */}
      <section className="space-y-3">
        <h3 className="card-heading">Booking-kanal</h3>
        <div>
          <label className="label">Hvor bookes bilen henne?</label>
          <select disabled={viewOnly} className={inp}
            value={form.operating_mode ?? 'platform'}
            onChange={e => set('operating_mode', e.target.value as Vehicle['operating_mode'])}>
            <option value="platform">Kun ekstern platform (GoMore/Getaround)</option>
            <option value="independent">Kun direkte booking (mit eget site)</option>
            <option value="both">Begge (synkroniseres via iCal)</option>
          </select>
        </div>

        {(form.operating_mode === 'platform' || form.operating_mode === 'both') && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Ekstern platform</label>
              <select disabled={viewOnly} className={inp}
                value={form.platform ?? ''}
                onChange={e => set('platform', (e.target.value || null) as Vehicle['platform'])}>
                <option value="">—</option>
                <option value="gomore">GoMore</option>
                <option value="getaround">Getaround</option>
              </select>
            </div>
            <div>
              <label className="label">Link til listing</label>
              <input type="url" disabled={viewOnly} className={inp}
                value={form.platform_listing_url ?? ''}
                onChange={e => set('platform_listing_url', e.target.value || null)}
                placeholder="https://gomore.dk/cars/..." />
            </div>
          </div>
        )}
      </section>

      {/* Publicering */}
      <section className="space-y-3 pt-2 border-t border-gray-100">
        <h3 className="card-heading">Publicering på mit eget site</h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" disabled={viewOnly}
            checked={!!form.public_listed}
            onChange={e => set('public_listed', e.target.checked)} />
          <span className="text-sm">Vis denne bil på det offentlige booking-site</span>
        </label>

        {form.public_listed && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">URL-slug</label>
                <input disabled={viewOnly} className={inp}
                  value={form.public_slug ?? ''}
                  onChange={e => set('public_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder={suggestSlug(vehicle)} />
                <p className="text-xs text-gray-400 mt-1">/biler/{form.public_slug || suggestSlug(vehicle)}</p>
              </div>
              <div>
                <label className="label">Pris pr. dag (DKK)</label>
                <input type="number" min="0" step="1" disabled={viewOnly} className={inp}
                  value={form.listing_price_per_day ?? ''}
                  onChange={e => set('listing_price_per_day', e.target.value === '' ? null : parseFloat(e.target.value))}
                  placeholder="495" />
              </div>
            </div>

            <div>
              <label className="label">Titel</label>
              <input disabled={viewOnly} className={inp}
                value={form.listing_title ?? ''}
                onChange={e => set('listing_title', e.target.value || null)}
                placeholder={`${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim()} />
            </div>

            <div>
              <label className="label">Beskrivelse</label>
              <textarea rows={4} disabled={viewOnly}
                className="input disabled:bg-gray-50 disabled:text-gray-600 resize-none"
                value={form.listing_description ?? ''}
                onChange={e => set('listing_description', e.target.value || null)}
                placeholder="Hvad gør denne bil særlig? Skriv 2-4 linjer som lejere ser på detalje-siden." />
            </div>

            <div>
              <label className="label">Features</label>
              <div className="flex flex-wrap gap-2">
                {FEATURE_OPTIONS.map(opt => {
                  const on = features.has(opt.value);
                  return (
                    <button key={opt.value} type="button" disabled={viewOnly}
                      onClick={() => toggleFeature(opt.value)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                        on
                          ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </section>

      {/* Galleri — supplerende billeder til carousel pa detalje-siden */}
      {form.public_listed && (
        <section className="space-y-3 pt-2 border-t border-gray-100">
          <h3 className="card-heading">Galleri</h3>
          <p className="text-xs text-gray-400 -mt-1">
            Hovedbilledet sættes under Stamdata. Disse vises i carousel på detalje-siden i den valgte rækkefølge.
          </p>
          <VehicleGallery vehicleId={vehicle.id!} viewOnly={viewOnly} />
        </section>
      )}

      {/* Pris-detalje — bruges af booking-flowet til at beregne totalpris */}
      {form.public_listed && (
        <section className="space-y-3 pt-2 border-t border-gray-100">
          <h3 className="card-heading">Pris-detalje</h3>
          <p className="text-xs text-gray-400 -mt-1">Alle beløb inkl. moms.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Inkluderet km/dag</label>
              <input type="number" min="0" step="50" disabled={viewOnly} className={inp}
                value={form.listing_km_per_day ?? ''}
                onChange={e => set('listing_km_per_day', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                placeholder="200" />
            </div>
            <div>
              <label className="label">Pris pr. ekstra km (DKK)</label>
              <input type="number" min="0" step="0.5" disabled={viewOnly} className={inp}
                value={form.listing_extra_km_price ?? ''}
                onChange={e => set('listing_extra_km_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="2,00" />
            </div>
            <div>
              <label className="label">Depositum (DKK)</label>
              <input type="number" min="0" step="100" disabled={viewOnly} className={inp}
                value={form.listing_deposit ?? ''}
                onChange={e => set('listing_deposit', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="5000" />
              <p className="text-xs text-gray-400 mt-1">Placeholder via Stripe — trækkes ikke faktisk.</p>
            </div>
            <div>
              <label className="label">Selvrisiko (DKK)</label>
              <input type="number" min="0" step="100" disabled={viewOnly} className={inp}
                value={form.listing_excess_amount ?? ''}
                onChange={e => set('listing_excess_amount', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="8000" />
            </div>
            <div>
              <label className="label">Selvrisiko-reduktion (DKK/dag)</label>
              <input type="number" min="0" step="5" disabled={viewOnly} className={inp}
                value={form.listing_excess_reduction_price ?? ''}
                onChange={e => set('listing_excess_reduction_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="75" />
              <p className="text-xs text-gray-400 mt-1">Lejer kan tilkøbe — reducerer selvrisiko til 0.</p>
            </div>
            <div>
              <label className="label">Ekstra fører (DKK/dag)</label>
              <input type="number" min="0" step="5" disabled={viewOnly} className={inp}
                value={form.listing_extra_driver_price ?? ''}
                onChange={e => set('listing_extra_driver_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="100" />
            </div>
          </div>
        </section>
      )}

      {/* Krav til lejer */}
      {form.public_listed && (
        <section className="space-y-3 pt-2 border-t border-gray-100">
          <h3 className="card-heading">Krav til lejer</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Min. alder</label>
              <input type="number" min="18" max="99" step="1" disabled={viewOnly} className={inp}
                value={form.listing_min_renter_age ?? ''}
                onChange={e => set('listing_min_renter_age', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                placeholder="23" />
            </div>
            <div>
              <label className="label">Min. antal år med kørekort</label>
              <input type="number" min="0" max="50" step="1" disabled={viewOnly} className={inp}
                value={form.listing_min_license_years ?? ''}
                onChange={e => set('listing_min_license_years', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                placeholder="2" />
            </div>
          </div>
        </section>
      )}

      {/* Pickup/aflevering */}
      {form.public_listed && (
        <section className="space-y-3 pt-2 border-t border-gray-100">
          <h3 className="card-heading">Afhentning / aflevering</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Standard afhentningstid</label>
              <input type="time" disabled={viewOnly} className={inp}
                value={form.listing_pickup_time ?? ''}
                onChange={e => set('listing_pickup_time', e.target.value || null)} />
            </div>
            <div>
              <label className="label">Standard afleveringstid</label>
              <input type="time" disabled={viewOnly} className={inp}
                value={form.listing_return_time ?? ''}
                onChange={e => set('listing_return_time', e.target.value || null)} />
            </div>
          </div>
          <div>
            <label className="label">Afhentnings-adresse</label>
            <input disabled={viewOnly} className={inp}
              value={form.listing_pickup_address ?? ''}
              onChange={e => set('listing_pickup_address', e.target.value || null)}
              placeholder="Brug min firma-adresse — eller skriv en specifik" />
            <p className="text-xs text-gray-400 mt-1">Lades dette tomt, vises firmaets adresse fra kunde-stamdata.</p>
          </div>
        </section>
      )}

      {/* Telematics */}
      <section className="space-y-3 pt-2 border-t border-gray-100">
        <h3 className="card-heading">Telematics (boks i bilen)</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Kilde</label>
            <select disabled={viewOnly} className={inp}
              value={form.telematics_source ?? 'none'}
              onChange={e => set('telematics_source', e.target.value as Vehicle['telematics_source'])}>
              <option value="none">Ingen</option>
              <option value="invers">Invers</option>
              <option value="2hire">2hire</option>
              <option value="oem">OEM (bilens egen)</option>
            </select>
          </div>
          {form.telematics_source !== 'none' && (
            <div>
              <label className="label">Device-ID</label>
              <input disabled={viewOnly} className={inp}
                value={form.telematics_device_id ?? ''}
                onChange={e => set('telematics_device_id', e.target.value || null)}
                placeholder="Boks-id eller serienummer" />
            </div>
          )}
        </div>
      </section>

      {!viewOnly && (
        <div className="flex justify-end pt-3">
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? '...' : 'Gem'}
          </button>
        </div>
      )}
    </form>
  );
}
